/**
 * @voltxampere/math — Dense matrix primitives
 *
 * Row-major `Float64Array` storage: element `(i, j)` lives at
 * `data[i * cols + j]`. Row-major matches the C and BLAS convention,
 * keeps cache lines warm for the inner loop of matrix–vector multiply
 * (the hot path in MNA solves), and lets us splice rows cheaply when
 * we add LU's row-pivoting permutation in `lu/dense-lu.ts`.
 *
 * Like the vector primitives, every operation here returns a fresh
 * matrix; the only mutating helper is `matrixSet`, which is the only
 * supported way to assemble a matrix piece-by-piece (Sprint 5 stamp
 * functions will use it).
 *
 * Indexed access uses `?? 0` to satisfy `noUncheckedIndexedAccess`
 * without resorting to non-null assertions (banned by the project
 * lint config). For an in-bounds read into `Float64Array` the
 * fallback never fires at runtime.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError } from "@voltxampere/shared";
function assertNonNegativeInt(value, name, op) {
    if (!Number.isInteger(value) || value < 0) {
        throw new ContractViolationError(`${op}: ${name} must be a non-negative integer, got ${String(value)}`);
    }
}
function assertDims(m, op) {
    if (m.data.length !== m.rows * m.cols) {
        throw new ContractViolationError(`${op}: data length ${String(m.data.length)} does not match rows*cols ${String(m.rows * m.cols)}`);
    }
}
function assertIndex(m, i, j, op) {
    if (!Number.isInteger(i) || i < 0 || i >= m.rows) {
        throw new ContractViolationError(`${op}: row index ${String(i)} out of range [0, ${String(m.rows)})`);
    }
    if (!Number.isInteger(j) || j < 0 || j >= m.cols) {
        throw new ContractViolationError(`${op}: column index ${String(j)} out of range [0, ${String(m.cols)})`);
    }
}
/** Create a fresh matrix; `fill` defaults to 0. */
export function createMatrix(rows, cols, fill = 0) {
    assertNonNegativeInt(rows, "rows", "createMatrix");
    assertNonNegativeInt(cols, "cols", "createMatrix");
    if (!Number.isFinite(fill)) {
        throw new ContractViolationError(`createMatrix: fill must be finite, got ${String(fill)}`);
    }
    const data = new Float64Array(rows * cols);
    if (fill !== 0)
        data.fill(fill);
    return { rows, cols, data };
}
/** Read element `(i, j)`. */
export function matrixGet(m, i, j) {
    assertIndex(m, i, j, "matrixGet");
    return m.data[i * m.cols + j] ?? 0;
}
/**
 * Set element `(i, j)`. The only mutating operation in the dense
 * matrix API; intended for stamp-style assembly. Callers that need
 * immutability should `clone(m)` first.
 */
export function matrixSet(m, i, j, value) {
    assertIndex(m, i, j, "matrixSet");
    if (!Number.isFinite(value)) {
        throw new ContractViolationError(`matrixSet: value must be finite, got ${String(value)} at (${String(i)}, ${String(j)})`);
    }
    m.data[i * m.cols + j] = value;
}
/** Return a deep copy. The returned matrix shares no buffer with `m`. */
export function matrixClone(m) {
    return { rows: m.rows, cols: m.cols, data: new Float64Array(m.data) };
}
function assertSameShape(a, b, op) {
    if (a.rows !== b.rows || a.cols !== b.cols) {
        throw new ContractViolationError(`${op}: shape mismatch (${String(a.rows)}x${String(a.cols)} vs ${String(b.rows)}x${String(b.cols)})`);
    }
}
/** Element-wise sum. */
export function matrixAdd(a, b) {
    assertDims(a, "matrixAdd");
    assertDims(b, "matrixAdd");
    assertSameShape(a, b, "matrixAdd");
    const data = new Float64Array(a.data.length);
    for (let k = 0; k < data.length; k++)
        data[k] = (a.data[k] ?? 0) + (b.data[k] ?? 0);
    return { rows: a.rows, cols: a.cols, data };
}
/** Standard matrix product `a · b`. Throws on inner-dimension mismatch. */
export function matrixMul(a, b) {
    assertDims(a, "matrixMul");
    assertDims(b, "matrixMul");
    if (a.cols !== b.rows) {
        throw new ContractViolationError(`matrixMul: inner dimensions disagree (${String(a.cols)} vs ${String(b.rows)})`);
    }
    const out = createMatrix(a.rows, b.cols);
    for (let i = 0; i < a.rows; i++) {
        for (let k = 0; k < a.cols; k++) {
            const aik = a.data[i * a.cols + k] ?? 0;
            if (aik === 0)
                continue;
            for (let j = 0; j < b.cols; j++) {
                out.data[i * b.cols + j] =
                    (out.data[i * b.cols + j] ?? 0) + aik * (b.data[k * b.cols + j] ?? 0);
            }
        }
    }
    return out;
}
/** Matrix–vector product `m · v`. */
export function matrixVectorMul(m, v) {
    assertDims(m, "matrixVectorMul");
    if (m.cols !== v.length) {
        throw new ContractViolationError(`matrixVectorMul: cols ${String(m.cols)} ≠ vector length ${String(v.length)}`);
    }
    const out = new Float64Array(m.rows);
    for (let i = 0; i < m.rows; i++) {
        let acc = 0;
        for (let j = 0; j < m.cols; j++)
            acc += (m.data[i * m.cols + j] ?? 0) * (v[j] ?? 0);
        out[i] = acc;
    }
    return out;
}
/** Transpose. Allocates a new matrix; never mutates `m`. */
export function matrixTranspose(m) {
    assertDims(m, "matrixTranspose");
    const out = createMatrix(m.cols, m.rows);
    for (let i = 0; i < m.rows; i++) {
        for (let j = 0; j < m.cols; j++) {
            out.data[j * m.rows + i] = m.data[i * m.cols + j] ?? 0;
        }
    }
    return out;
}
/** `n × n` identity matrix. */
export function matrixIdentity(n) {
    const m = createMatrix(n, n);
    for (let i = 0; i < n; i++)
        m.data[i * n + i] = 1;
    return m;
}
/**
 * Element-wise approximate equality. Like {@link matrixGet} this is
 * absolute-tolerance based; reach for `equalsWithTol` from the
 * tolerance module when you need SPICE's mixed reltol/abstol.
 */
export function matrixEquals(a, b, tol = 0) {
    if (a.rows !== b.rows || a.cols !== b.cols)
        return false;
    if (!Number.isFinite(tol) || tol < 0) {
        throw new ContractViolationError(`matrixEquals: tol must be a non-negative finite number, got ${String(tol)}`);
    }
    for (let k = 0; k < a.data.length; k++) {
        if (Math.abs((a.data[k] ?? 0) - (b.data[k] ?? 0)) > tol)
            return false;
    }
    return true;
}
/** Diagnostic helper — `true` iff every element is finite. */
export function isFiniteMatrix(m) {
    for (const v of m.data) {
        if (!Number.isFinite(v))
            return false;
    }
    return true;
}
//# sourceMappingURL=matrix.js.map